"use client";

import { AreaChartWithGradient } from "@/components";
import { usePriceHistory } from "@/hooks/usePriceHistory";
import type { Listing } from "@/services/api";
import React, { useEffect, useState } from "react";

interface WishlistChartProps {
    listing: Listing;
    variant?: 'full' | 'compact';
    className?: string;
}

const WishlistChart: React.FC<WishlistChartProps> = React.memo(({
    listing,
    variant = 'full',
    className = ''
}) => {
    const [shouldLoadData, setShouldLoadData] = useState(false);

    // Delay data loading slightly to prevent loading charts for items that scroll by quickly
    useEffect(() => {
        const timer = setTimeout(() => setShouldLoadData(true), 100);
        return () => clearTimeout(timer);
    }, []);

    const {
        priceHistoryData,
        historicalPriceData,
        similarListingsCount,
        soldListingsCount,
        isLoading,
        error
    } = usePriceHistory(shouldLoadData ? listing._id : null, {
        listing: shouldLoadData ? listing : undefined,
        includeCurrentPrice: true
    });

    const isCompact = variant === 'compact';
    const chartHeight = isCompact ? 'h-[120px]' : 'h-[200px]';
    const spinnerSize = isCompact ? 'h-6 w-6' : 'h-8 w-8';
    const textSize = isCompact ? 'text-xs' : 'text-sm';

    const renderChart = () => {
        if (isLoading) {
            return (
                <div className={`bg-white ${chartHeight} flex items-center justify-center`}>
                    <div className={`animate-spin rounded-full ${spinnerSize} border-b-2 border-black`}></div>
                </div>
            );
        }

        if (error) {
            return (
                <div className={`bg-white ${chartHeight} flex items-center justify-center`}>
                    <div className={`text-gray-400 ${textSize} text-center`}>
                        <p>Unable to load price history</p>
                        {!isCompact && <p className="text-xs mt-1">Chart data unavailable</p>}
                    </div>
                </div>
            );
        }

        if (priceHistoryData.length === 0 && historicalPriceData.length === 0) {
            return (
                <div className={`bg-white ${chartHeight} flex items-center justify-center`}>
                    <div className={`text-gray-400 ${textSize} text-center`}>
                        <p>No price history available</p>
                        {!isCompact && <p className="text-xs mt-1">Chart will appear as data becomes available</p>}
                    </div>
                </div>
            );
        }

        return (
            <AreaChartWithGradient
                data={priceHistoryData}
                historicalData={historicalPriceData}
                similarCount={similarListingsCount}
                soldCount={soldListingsCount}
                compact={isCompact}
                className={className}
            />
        );
    };

    return (
        <div className="bg-white">
            {renderChart()}
        </div>
    );
});

WishlistChart.displayName = 'WishlistChart';

export default WishlistChart; 